/**
 * The editor's check of `.apiboo/launch.json` before Save — the same rules
 * and messages as Rust `run_doctor/files.rs validate_launch_text`, which
 * checks again before writing (Rust is the authority; this only shows the
 * errors inline without a round trip). → [] when valid.
 */
const KEYS = new Set(['name', 'runtimeExecutable', 'runtimeArgs', 'port', 'cwd', 'env']);
const ENV_NAME = /^[A-Za-z_][A-Za-z0-9_]*$/;

export function launchErrors(text) {
  let root;
  try {
    root = JSON.parse(text);
  } catch (e) {
    return [`invalid_json: ${e.message}`];
  }
  const errors = [];
  if (!root || typeof root !== 'object' || Array.isArray(root)) return ['schema: the file must be a JSON object'];
  Object.keys(root).forEach((k) => {
    if (!['version', 'configurations', '$schema'].includes(k)) errors.push(`schema: unknown key "${k}"`);
  });
  if ('version' in root && typeof root.version !== 'string') errors.push('schema: version must be a string');
  if (!Array.isArray(root.configurations)) {
    errors.push('schema: configurations must be a list');
    return errors;
  }
  const names = new Set();
  root.configurations.forEach((e, i) => {
    const at = `configurations[${i}]`;
    if (!e || typeof e !== 'object' || Array.isArray(e)) {
      errors.push(`schema: ${at} must be an object`);
      return;
    }
    Object.keys(e).forEach((k) => {
      if (!KEYS.has(k)) errors.push(`schema: ${at}: unknown key "${k}"`);
    });
    ['name', 'runtimeExecutable'].forEach((k) => {
      if (!(k in e)) errors.push(`schema: ${at}.${k} is required`);
      else if (typeof e[k] !== 'string' || !e[k].trim()) errors.push(`schema: ${at}.${k} must be a non-empty string`);
    });
    if ('runtimeArgs' in e && !(Array.isArray(e.runtimeArgs) && e.runtimeArgs.every((a) => typeof a === 'string'))) {
      errors.push(`schema: ${at}.runtimeArgs must be a list of strings`);
    }
    if ('port' in e && !(Number.isInteger(e.port) && e.port >= 1 && e.port <= 65535)) {
      errors.push(`schema: ${at}.port must be a number from 1 to 65535`);
    }
    if ('cwd' in e) {
      if (typeof e.cwd !== 'string') errors.push(`schema: ${at}.cwd must be a string`);
      else if (e.cwd.split(/[\\/]/).includes('..')) errors.push(`schema: ${at} (${e.name}): cwd_escapes`);
      else if (/^([A-Za-z]:|[\\/])/.test(e.cwd)) errors.push(`schema: ${at} (${e.name}): absolute_cwd`);
    }
    if ('env' in e) {
      const env = e.env;
      if (!env || typeof env !== 'object' || Array.isArray(env) || !Object.values(env).every((v) => typeof v === 'string')) {
        errors.push(`schema: ${at}.env must be an object of strings`);
      } else {
        Object.keys(env).forEach((k) => {
          if (!ENV_NAME.test(k)) errors.push(`schema: ${at} (${e.name}): bad_env_name:${k}`);
        });
      }
    }
    if (typeof e.name === 'string' && e.name) {
      if (names.has(e.name)) errors.push(`schema: ${at}: duplicate name "${e.name}"`);
      names.add(e.name);
    }
  });
  return errors;
}

/** A starting file: the configurations given (suggestions), or an empty list. */
export function launchTemplate(configurations = []) {
  return `${JSON.stringify({ version: '0.0.1', configurations }, null, 2)}\n`;
}
